// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  requestNotificationPermission,
  onForegroundMessage,
} from '../utils/firebase-messaging';
import { editClient } from '../api/clientApi';
import { getNotificationsByClientId } from '../api/notificationApi';

// Default context shape
const AuthContext = createContext({
  clientData: null,
  notifications: [],
  authenticate: () => {},
  logout: () => {},
  setclientData: () => {},
  fetchNotifications: () => {},
  refreshNotificationToken: () => {},
});

// Custom hook
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Load client data from localStorage with fallback
  const [clientData, setclientData] = useState(() => {
    try {
      const stored = localStorage.getItem('client');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const [notifications, setNotifications] = useState([]);
  const initCalledRef = useRef(false); // Avoid multiple FCM refreshes

  // Load notifications (stub)
  const loadNotifications = useCallback(async (clientId) => {
    try {
      const notifData = await getNotificationsByClientId(clientId);
      setNotifications(notifData?.notifications || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }, []);

  // Refresh FCM token and update client on server
  const refreshNotificationToken = useCallback(async () => {
    if (!clientData) return;
    try {
      const token = await requestNotificationPermission();
console.log("refreshNotificationToken callback", token, clientData);
const payload={...clientData,fcm_token: token,}
      const updated = await editClient(payload);

      // Merge new values with old state in case response is partial
      setclientData((prev) => ({ ...prev, ...updated }));
    } catch (error) {
      console.error('Error refreshing FCM token:', error);
    }
  }, [clientData]);

  // Persist client and handle notifications/token setup
  useEffect(() => {
    if (clientData) {
      console.log("updating localstorage client data")
      localStorage.setItem('client', JSON.stringify(clientData));
      loadNotifications(clientData?.client_id);

      if (!initCalledRef.current) {
        initCalledRef.current = true;
        refreshNotificationToken();
        onForegroundMessage(); // Optionally handle foreground messages here
      }
    } else {
      localStorage.removeItem('client');
      setNotifications([]);
    }
  }, [clientData, loadNotifications, refreshNotificationToken]);

  // Login
  const authenticate = useCallback((client) => {
    setclientData(client);
  }, []);

  // Logout
  const logout = useCallback(() => {
    setclientData(null);
    setNotifications([]);
    // Optionally: unsubscribe from FCM topics or messaging
  }, []);

  const value = useMemo(
    () => ({
      clientData,
      notifications,
      authenticate,
      logout,
      setclientData,
      fetchNotifications: loadNotifications,
      refreshNotificationToken,
    }),
    [
      clientData,
      notifications,
      authenticate,
      logout,
      loadNotifications,
      refreshNotificationToken,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
